import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { Search, Bell, Settings, CheckCircle2, AlertCircle, Save } from "lucide-react";
import { adminApi } from "../../services/api";

const INITIAL_FORM = {
  platformName: "",
  defaultPoolSize: "",
  defaultEntryAmount: "",
};

export default function AdminSettings() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;

    adminApi.getSettings()
      .then(({ data }) => {
        if (!active) return;
        setForm({
          platformName: data.data.platformName,
          defaultPoolSize: String(data.data.defaultPoolSize),
          defaultEntryAmount: String(data.data.defaultEntryAmount),
        });
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || "Unable to load platform settings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, []);

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setSaved(false);
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError("");

    try {
      const { data } = await adminApi.updateSettings(form);
      setForm({
        platformName: data.data.platformName,
        defaultPoolSize: String(data.data.defaultPoolSize),
        defaultEntryAmount: String(data.data.defaultEntryAmount),
      });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save platform settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Settings</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search settings..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>
        <div className="admin-content">
          <section className="admin-panel" style={{ maxWidth: 560, padding: 0 }}>
            <div className="panel-heading">
              <h2><Settings size={18} /> Platform Settings</h2>
            </div>
            <form style={{ display: "grid", gap: 20, padding: 24 }} onSubmit={handleSubmit}>
              {loading && <div className="admin-empty">Loading settings...</div>}
              {error && (
                <div className="auth-error admin-auth-error">
                  <AlertCircle size={15} /> {error}
                </div>
              )}
              {saved && (
                <div className="auth-success" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckCircle2 size={14} /> Settings saved successfully.
                </div>
              )}
              <div className="auth-field">
                <label>Platform Name</label>
                <input name="platformName" type="text" minLength={2} maxLength={80} required value={form.platformName} onChange={handleChange} disabled={loading || saving} />
              </div>
              <div className="settings-field-grid">
                <div className="auth-field">
                  <label>Default Pool Size</label>
                  <input name="defaultPoolSize" type="number" min={1} max={100000} step={1} required value={form.defaultPoolSize} onChange={handleChange} disabled={loading || saving} />
                </div>
                <div className="auth-field">
                  <label>Entry Amount ($)</label>
                  <input name="defaultEntryAmount" type="number" step="0.01" min={0} max={1000000} required value={form.defaultEntryAmount} onChange={handleChange} disabled={loading || saving} />
                </div>
              </div>
              <button type="submit" className="auth-submit" style={{ maxWidth: 200 }} disabled={loading || saving}>
                <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
              </button>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
}
